import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import DOMPurify from 'dompurify';
import { parentApi, type ParentMessage } from '../../lib/parentApi';
import {
  Badge,
  Button,
  EmptyTableRow,
  ListToolbar,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea
} from '../../components/ui';

function formatDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString('de-DE');
}

export default function ParentMessages() {
  const [selected, setSelected] = useState<ParentMessage | null>(null);
  const { data: children = [] } = useQuery(['parent-children'], () => parentApi.children());
  const { data: messages = [], isLoading, refetch } = useQuery(['parent-messages'], () => parentApi.messages());

  const childNameByUnid = useMemo(() => {
    return new Map(
      children.map((child) => [
        child.sophomorixUnid,
        `${child.Vorname} ${child.Name} (${child.Klasse})`
      ] as const)
    );
  }, [children]);

  return (
    <div className="space-y-4">
      <ListToolbar
        title="E-Mails und Nachrichten"
        description="Alle Elternbriefe und individuellen E-Mails in einer reinen Lesesicht."
        trailing={
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
            Aktualisieren
          </Button>
        }
      />

      <TableScrollArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Typ</TableHeaderCell>
              <TableHeaderCell>Titel</TableHeaderCell>
              <TableHeaderCell>Kind</TableHeaderCell>
              <TableHeaderCell>Datum</TableHeaderCell>
              <TableHeaderCell>Aktion</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && messages.length === 0 ? (
              <EmptyTableRow colSpan={5} title="Keine Nachrichten vorhanden." />
            ) : null}
            {messages.map((message) => (
              <TableRow key={message.message_id}>
                <TableCell>
                  <Badge variant={message.message_type === 'elternbrief' ? 'info' : 'neutral'}>
                    {message.message_type === 'elternbrief' ? 'Elternbrief' : 'E-Mail'}
                  </Badge>
                </TableCell>
                <TableCell>{message.title}</TableCell>
                <TableCell>{childNameByUnid.get(message.student_unid) || message.student_unid}</TableCell>
                <TableCell>{formatDate(message.created_at)}</TableCell>
                <TableCell>
                  <Button type="button" size="sm" variant="outline" onClick={() => setSelected(message)}>
                    Lesen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableScrollArea>

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.title || 'Nachricht'}
        description={selected ? formatDate(selected.created_at) : undefined}
        size="xl"
      >
        {selected?.body_html ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.body_html) }}
          />
        ) : (
          <div className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">
            {selected?.body_text || 'Kein Inhalt vorhanden.'}
          </div>
        )}
      </Modal>
    </div>
  );
}
